/**
 * src/features/billing/ui/UpgradePromptModal.tsx
 *
 * Non-destructive upgrade prompt modal for Pro features.
 * Wraps ResponsiveModal (mandatory modal primitive — CLAUDE.md + UI-SPEC).
 *
 * Feature prop controls the body copy:
 *   'sync'      → billing.upgrade_locked_sync_body
 *   'analytics' → billing.upgrade_locked_analytics_body
 *
 * Checkout flow: CTA POSTs /api/billing/checkout (08-02) → redirects to
 * the returned Stripe Checkout URL (same tab). Failure shows role=alert;
 * CTA is restored to idle. Dismissing leaves user data and queue untouched.
 *
 * T-08-19: modal dismissal never grants access — server 402 is authoritative.
 * T-08-20: open state is separate from sync queue — no data cleared here.
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import Button from '@/shared/ui/button';
import { useUpgradeModal } from '../lib/useUpgradeModal';
import { IntervalToggle, type BillingInterval } from './IntervalToggle';

export interface UpgradePromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: 'sync' | 'analytics';
}

export const UpgradePromptModal: FC<UpgradePromptModalProps> = ({
  open,
  onOpenChange,
  feature,
}) => {
  const { t } = useTranslation();
  const { loading, error, handleCheckout } = useUpgradeModal();
  const [interval, setInterval] = useState<BillingInterval>('monthly');

  const bodyKey =
    feature === 'sync'
      ? 'billing.upgrade_locked_sync_body'
      : 'billing.upgrade_locked_analytics_body';

  const bodyFallback =
    feature === 'sync'
      ? 'Cloud sync is available in the Pro plan.'
      : 'Margin analytics is available in the Pro plan.';

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('billing.upgrade_modal_title', 'Pro Feature')}
      className="max-w-[400px]"
      stickyHeader={false}
      dismissible={true}
    >
      <div className="flex flex-col gap-4 p-2">
        {/* Feature-specific body copy */}
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t(bodyKey, bodyFallback)}
        </p>

        {/* Pro feature checklist */}
        <ul className="flex flex-col gap-2">
          <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 shrink-0" />
            {t('billing.feature_sync', 'Sync across all devices')}
          </li>
          <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 shrink-0" />
            {t('billing.feature_analytics', 'Margin analytics')}
          </li>
        </ul>

        {/* Interval + price block — mirrors PaywallPage so both checkout entry
            points present the same "choose interval → see price" module. */}
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 flex flex-col gap-3">
          <IntervalToggle value={interval} onChange={setInterval} disabled={loading} />

          {/* Price for the selected interval. Savings line is always rendered
              (fades in for annual) so toggling never shifts the CTA below. */}
          <div className="flex flex-col gap-0.5">
            <span className="text-subhead font-bold text-slate-900 dark:text-slate-100">
              {interval === 'annual'
                ? t('billing.price_annual', '499 PLN/yr')
                : t('billing.price_monthly', '49 PLN/mo')}
            </span>
            <span
              className={`text-caption font-medium text-green-600 dark:text-green-400 transition-opacity duration-200 ${
                interval === 'annual' ? 'opacity-100' : 'opacity-0'
              }`}
              aria-hidden={interval !== 'annual'}
            >
              {t('billing.annual_savings', '−15% · ≈1.8 months free')}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2 mt-1">
          <Button
            variant="default"
            size="lg"
            className="w-full"
            onClick={() => handleCheckout(interval)}
            disabled={loading}
            aria-busy={loading}
          >
            {loading
              ? t('billing.checkout_loading', 'Opening checkout...')
              : t('billing.upgrade_cta', 'Upgrade to Pro')}
          </Button>
          <Button
            variant="ghost"
            size="default"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            {t('billing.maybe_later', 'Maybe later')}
          </Button>
        </div>

        {/* Checkout error (role=alert for screen readers) */}
        {error && (
          <p className="text-sm text-destructive text-center" role="alert">
            {t('billing.checkout_error', 'Could not open checkout. Please try again.')}
          </p>
        )}
      </div>
    </ResponsiveModal>
  );
};
